import {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useQueryClient,
} from 'react-query';

import { AxiosError, AxiosResponse } from 'axios';

import { IErrorResponse } from '../../../store/redux-api/apiResponseWrapper';

export type QueryOptions = UseQueryOptions<AxiosResponse<any>, AxiosError<any>>;

export const defaultQueryConfig: QueryOptions = {
  retry: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
};

export interface IUseAxiosQueryResult<TR, TE = IErrorResponse>
  extends Omit<UseQueryResult<AxiosResponse<TR>, AxiosError<TE>>, 'data'>,
    Pick<AxiosResponse<TR>, 'headers'> {
  data: TR | undefined;
  /** Cancels current fetch of the query and restores it to previous state */
  cancelQuery: () => Promise<void>;
}

/** Creates a useAxiosQuery hook with customizable options applied. */
export const makeUseAxiosQuery =
  (defaultOptions: QueryOptions = {}) =>
  <TR, TE = IErrorResponse>(
    key: QueryKey,
    queryFunction?: QueryFunction<AxiosResponse<TR>>,
    config?: UseQueryOptions<AxiosResponse<TR>, AxiosError<TE>>
  ): IUseAxiosQueryResult<TR, TE> => {
    const queryClient = useQueryClient();
    const { data: response, ...restQuery } = useQuery<AxiosResponse<TR>, AxiosError<TE>>(
      key,
      queryFunction,
      {
        ...defaultOptions,
        ...config,
      }
    );

    return {
      ...restQuery,
      data: response?.data,
      headers: response?.headers,
      cancelQuery: () => queryClient.cancelQueries({ queryKey: key }),
    };
  };

export const useAxiosQuery = makeUseAxiosQuery(defaultQueryConfig);

export default useAxiosQuery;
